/**
 * SCRUM-225 — Routing unificado de cobros SaaS.
 * Resuelve provider por operación + método + capabilities + configuración + prioridad.
 * No es un segundo motor: reutiliza payment-providers + saas-billing-config.
 */
import { getProvider, type PaymentProviderId } from "@/lib/payment-providers";
import type {
  PackGateway,
  SaasSelfServiceConfig,
  SubscriptionGateway,
} from "@/lib/saas-billing-config";

/** Operaciones de cobro SaaS (Structura → tenant). */
export type SaasBillingOperation = "monthly_subscription" | "annual" | "pack";

/** Métodos que ve el cliente — nunca nombres de gateway. */
export type SaasCheckoutPaymentMethod = "credit_card" | "debit_card" | "bank_transfer";

export const SAAS_CHECKOUT_METHOD_LABELS: Record<SaasCheckoutPaymentMethod, string> = {
  credit_card: "Tarjeta de crédito",
  debit_card: "Tarjeta de débito",
  bank_transfer: "Transferencia bancaria",
};

export type SaasRoutableProvider = SubscriptionGateway | "manual_transfer";

export type SaasProviderAvailability = Partial<
  Record<SaasRoutableProvider, { enabled: boolean; configured: boolean; supportsRecurring?: boolean }>
>;

export type SaasOperationMethodPriority = Partial<
  Record<SaasBillingOperation, Partial<Record<SaasCheckoutPaymentMethod, SaasRoutableProvider[]>>>
>;

const DEFAULT_CARD_ORDER: SaasRoutableProvider[] = ["mercadopago", "uala", "payway", "dlocal"];
const DEFAULT_TRANSFER_ORDER: SaasRoutableProvider[] = ["manual_transfer"];
const DEFAULT_PACK_CARD_ORDER: SaasRoutableProvider[] = ["payway", "uala"];

export const DEFAULT_OPERATION_METHOD_PRIORITY: Required<
  Record<SaasBillingOperation, Record<SaasCheckoutPaymentMethod, SaasRoutableProvider[]>>
> = {
  monthly_subscription: {
    credit_card: [...DEFAULT_CARD_ORDER],
    debit_card: [...DEFAULT_CARD_ORDER],
    bank_transfer: [...DEFAULT_TRANSFER_ORDER],
  },
  annual: {
    credit_card: [...DEFAULT_CARD_ORDER],
    debit_card: [...DEFAULT_CARD_ORDER],
    bank_transfer: [...DEFAULT_TRANSFER_ORDER],
  },
  pack: {
    credit_card: [...DEFAULT_PACK_CARD_ORDER],
    debit_card: [...DEFAULT_PACK_CARD_ORDER],
    bank_transfer: [...DEFAULT_TRANSFER_ORDER],
  },
};

function catalogSupportsRecurring(provider: SaasRoutableProvider): boolean {
  if (provider === "manual_transfer") return false;
  return Boolean(getProvider(provider as PaymentProviderId)?.capabilities.recurring);
}

function providerSupportsMethod(
  provider: SaasRoutableProvider,
  method: SaasCheckoutPaymentMethod,
): boolean {
  if (method === "bank_transfer") return provider === "manual_transfer";
  if (provider === "manual_transfer") return false;
  return Boolean(getProvider(provider as PaymentProviderId)?.capabilities.cards);
}

/**
 * Prioridad efectiva para operación×método.
 * Si no hay override, usa defaults + preferred subscription / pack priority legacy.
 */
export function resolveOperationMethodPriority(
  selfService: SaasSelfServiceConfig | undefined | null,
  operation: SaasBillingOperation,
  method: SaasCheckoutPaymentMethod,
): SaasRoutableProvider[] {
  const override = selfService?.operationMethodPriority?.[operation]?.[method];
  if (Array.isArray(override) && override.length > 0) {
    return [...override];
  }

  const base = [...DEFAULT_OPERATION_METHOD_PRIORITY[operation][method]];

  if (operation === "monthly_subscription" || operation === "annual") {
    if (method === "credit_card" || method === "debit_card") {
      const preferred = selfService?.subscriptionPreferredGateway ?? "mercadopago";
      return [preferred, ...base.filter((p) => p !== preferred)];
    }
  }

  if (operation === "pack" && (method === "credit_card" || method === "debit_card")) {
    const pack = (selfService?.packGatewayPriority ?? ["payway", "uala"]) as PackGateway[];
    return [...pack, ...base.filter((p) => !pack.includes(p as PackGateway))];
  }

  return base;
}

/**
 * SCRUM-225: kill-switch (`selfService.enabled`) + override opcional por gateway.
 * Missing/undefined toggle = ON (compat). Solo bloquea si el flag está explícitamente `false`.
 * Cobros UI puede no editar estos toggles; no inventar un segundo motor de elegibilidad.
 */
export function isSaasSelfServiceGatewayOn(
  selfService: SaasSelfServiceConfig | undefined | null,
  provider: SaasRoutableProvider,
): boolean {
  if (!selfService) return true;
  if (selfService.enabled === false) return false;
  const gw = selfService.gateways;
  if (!gw) return true;
  if (provider === "payway") return gw.payway !== false;
  if (provider === "uala") return gw.uala !== false;
  if (provider === "dlocal") return gw.dlocal !== false;
  if (provider === "manual_transfer") return gw.manualTransferArs !== false;
  // mercadopago: sin toggle legacy por gateway
  return true;
}

export type ResolveSaasProviderInput = {
  operation: SaasBillingOperation;
  method: SaasCheckoutPaymentMethod;
  selfService?: SaasSelfServiceConfig | null;
  availability: SaasProviderAvailability;
  /** Si true (default), exige selfService.enabled !== false. */
  requireSelfServiceOn?: boolean;
};

export type ResolveSaasProviderResult = {
  provider: SaasRoutableProvider | null;
  candidates: SaasRoutableProvider[];
  rejected: Array<{ provider: SaasRoutableProvider; reason: string }>;
};

/**
 * Elige el primer provider elegible respetando prioridad y fallback.
 * Mensual (suscripción): exige soporte real de recurring (catálogo + availability).
 */
export function resolveSaasPaymentProvider(input: ResolveSaasProviderInput): ResolveSaasProviderResult {
  const requireSs = input.requireSelfServiceOn !== false;
  const rejected: ResolveSaasProviderResult["rejected"] = [];
  const priority = resolveOperationMethodPriority(input.selfService, input.operation, input.method);
  const candidates: SaasRoutableProvider[] = [];

  if (requireSs && input.selfService?.enabled === false) {
    return {
      provider: null,
      candidates: [],
      rejected: priority.map((p) => ({ provider: p, reason: "self_service_off" })),
    };
  }

  for (const provider of priority) {
    if (!providerSupportsMethod(provider, input.method)) {
      rejected.push({ provider, reason: "method_unsupported" });
      continue;
    }
    if (!isSaasSelfServiceGatewayOn(input.selfService, provider)) {
      rejected.push({ provider, reason: "gateway_disabled" });
      continue;
    }

    const avail = input.availability[provider];
    if (!avail?.enabled) {
      rejected.push({ provider, reason: "not_enabled" });
      continue;
    }
    if (!avail.configured) {
      rejected.push({ provider, reason: "not_configured" });
      continue;
    }

    if (input.operation === "monthly_subscription") {
      const catalogOk = catalogSupportsRecurring(provider);
      const runtimeOk = avail.supportsRecurring !== false && (avail.supportsRecurring === true || catalogOk);
      // Exigir recurring real: catálogo + flag runtime cuando existe
      if (!catalogOk) {
        rejected.push({ provider, reason: "no_recurring_capability" });
        continue;
      }
      if (avail.supportsRecurring === false) {
        rejected.push({ provider, reason: "recurring_unavailable" });
        continue;
      }
      if (!runtimeOk && avail.supportsRecurring !== true) {
        // catalogOk already true; allow if supportsRecurring undefined
      }
    }

    candidates.push(provider);
  }

  return {
    provider: candidates[0] ?? null,
    candidates,
    rejected,
  };
}

/** Orden de gateways para suscripción (compat GET /api/billing/subscribe). */
export function buildSubscriptionGatewayOrder(input: {
  selfService?: SaasSelfServiceConfig | null;
  available: Partial<Record<SubscriptionGateway, boolean>>;
  recurringAvailable?: Partial<Record<SubscriptionGateway, boolean>>;
  /** monthly_recurring filtra sin recurring. */
  requireRecurring?: boolean;
}): SubscriptionGateway[] {
  const availability: SaasProviderAvailability = {};
  for (const id of ["mercadopago", "payway", "uala", "dlocal"] as const) {
    availability[id] = {
      enabled: true,
      configured: Boolean(input.available[id]),
      supportsRecurring: input.recurringAvailable?.[id] ?? getProvider(id)?.capabilities.recurring,
    };
  }

  const method: SaasCheckoutPaymentMethod = "credit_card";
  const resolved = resolveSaasPaymentProvider({
    operation: input.requireRecurring ? "monthly_subscription" : "annual",
    method,
    selfService: input.selfService,
    availability,
    requireSelfServiceOn: false,
  });

  return resolved.candidates.filter((p): p is SubscriptionGateway => p !== "manual_transfer");
}

/**
 * SCRUM-225 — providers que PUEDEN ofrecerse para operación × método según capacidades (no mira la config guardada):
 * soporta el método, y para mensual tiene recurring real. `requireConfigured` además exige habilitado + configurado.
 */
export function eligibleProvidersFor(
  operation: SaasBillingOperation,
  method: SaasCheckoutPaymentMethod,
  availability: SaasProviderAvailability,
  opts: { requireConfigured?: boolean } = {},
): SaasRoutableProvider[] {
  const all: SaasRoutableProvider[] = ["mercadopago", "uala", "payway", "dlocal", "manual_transfer"];
  return all.filter((provider) => {
    if (!providerSupportsMethod(provider, method)) return false;
    if (operation === "monthly_subscription") {
      if (!catalogSupportsRecurring(provider)) return false;
      if (availability[provider]?.supportsRecurring === false) return false;
    }
    if (opts.requireConfigured) {
      const a = availability[provider];
      if (!a?.enabled || !a.configured) return false;
    }
    return true;
  });
}

/** Self-service ON solo si hay al menos un provider habilitado y configurado. */
export function selfServiceCanBeEnabled(availability: SaasProviderAvailability): boolean {
  return Object.values(availability).some((a) => a?.enabled && a.configured);
}

export type RoutingConfigError = { code: "self_service_no_provider" | "provider_not_eligible"; operation?: SaasBillingOperation; method?: SaasCheckoutPaymentMethod; provider?: SaasRoutableProvider };

/**
 * Valida un PATCH de self-service contra la disponibilidad real. Solo se evalúan los cambios respecto de `previous`
 * (datos viejos incompatibles no bloquean guardar otra cosa): activar con ningún provider listo, o agregar a una
 * prioridad un provider que no soporta ese método / no tiene recurring para mensual.
 */
export function validateSelfServiceChange(
  previous: SaasSelfServiceConfig | undefined | null,
  next: SaasSelfServiceConfig,
  availability: SaasProviderAvailability,
): RoutingConfigError[] {
  const errors: RoutingConfigError[] = [];
  if (next.enabled !== false && previous?.enabled === false && !selfServiceCanBeEnabled(availability)) {
    errors.push({ code: "self_service_no_provider" });
  }
  const ops: SaasBillingOperation[] = ["monthly_subscription", "annual", "pack"];
  const methods: SaasCheckoutPaymentMethod[] = ["credit_card", "debit_card", "bank_transfer"];
  for (const operation of ops) {
    for (const method of methods) {
      const list = next.operationMethodPriority?.[operation]?.[method];
      if (!Array.isArray(list)) continue;
      const before = new Set(previous?.operationMethodPriority?.[operation]?.[method] ?? []);
      const allowed = new Set(eligibleProvidersFor(operation, method, availability));
      for (const provider of list) {
        if (!before.has(provider) && !allowed.has(provider)) errors.push({ code: "provider_not_eligible", operation, method, provider });
      }
    }
  }
  return errors;
}
